/**
 * What is waiting for the person signed in, collected for the bell in the top bar.
 *
 * # Why a bell at all
 *
 * A discount over a receptionist's own ceiling is stored as a request and decided by
 * somebody senior (docs/BLAGAJNA.md). Until now the request was drawn in exactly two
 * places: on the folio card it came off, and at the top of the "Popusti in umiki"
 * report. Both of them have to be OPENED. So a manager learned that somebody was
 * waiting when they happened to look at that guest's bill, and the receptionist who
 * asked learned the answer the same way — which on a busy morning means she quoted a
 * price nobody had agreed to, or asked again.
 *
 * The bell is the one place in the portal where things waiting for a PERSON collect. It
 * is not the chessboard's "Zahteva obravnavo" strip, which collects what is waiting on a
 * ROOM, and it is not the sidebar's reminders, which are dated notes somebody wrote for
 * themselves. Three different questions; keeping them apart is what makes each of them
 * worth opening.
 *
 * # The two halves
 *
 *   - the requests this account may DECIDE. The service narrows that list to what the
 *     reader's own ceiling covers, which is the same rule the card's buttons use — one
 *     place decides who may press, so the bell can never offer a decision the card
 *     refuses. It is work: it stays until it is answered.
 *   - the answers to requests this account MADE. That is news, so it is counted until
 *     the bell is opened and gone afterwards. The watermark is per browser, in
 *     localStorage, because "have I seen this" is a fact about a person at a screen and
 *     not about the hotel.
 *
 * # Adding a third
 *
 * Write a builder that answers `INotification[]`, add its kind to `enums/notification`,
 * and add it to `sources` below. Nothing in `NotificationBell.vue` knows what a discount
 * is, and nothing in it should have to know what the next kind is either.
 */
import { computed, onUnmounted, ref } from 'vue'

import { FolioEventStatus } from '@/enums/folio'
import { NotificationKind } from '@/enums/notification'
import { StaffAbsenceState } from '@/enums/staffAbsence'
import dayjs, { DATE_FORMAT, formatDateTime } from '@/libs/dayjs'
import { formatTaxRate } from '@/libs/folio'
import i18n from '@/libs/i18n'
import { formatAmount } from '@/libs/money'
import {
    acknowledgeShiftChangeInPerson,
    shiftChangeDeclineLine,
    shiftChangeDeclined,
    shiftChangeForPerson,
    shiftChangeLine,
    shiftChangeTag,
    shiftChangeWithdrawn,
    weekStart,
} from '@/libs/staff'
import {
    absenceKindIcon,
    absenceKindLabel,
    loadAbsenceRequests,
    loadMyAbsenceRequests,
} from '@/libs/staffAbsence'
import {
    formatDays,
    isAcknowledged,
    loadMyVacationDecisions as loadMyUnacknowledgedDecisions,
} from '@/libs/staffVacation'
import { useAppStore } from '@/stores/app'
import { useFetchStore } from '@/stores/fetch'
import type { IFolioEvent } from '@/types/Folio'
import type { INotification } from '@/types/Notification'
import type { IStaffMyRota, IStaffRota } from '@/types/Staff'
import type { IStaffAbsenceRequest } from '@/types/StaffAbsence'

/** How often a tab nobody touches asks again. */
const EVERY_MS = 60 * 1000

/**
 * The shortest gap between two reads. The bell refreshes on a route change as well as
 * on the clock, and a desk clicking through four screens in five seconds should not
 * produce four rounds of reads.
 */
const MIN_GAP_MS = 10 * 1000

/**
 * When this browser last had the bell open, as an ISO instant.
 *
 * Per browser and not per account on purpose: it decides nothing but whether a line is
 * still called new, and the cost of being wrong is one item shown twice. An account
 * signing in on a machine somebody else used sees at worst a decision it has already
 * read; the alternative — a field on the user record — would be a write on every
 * opening of a popover.
 */
const SEEN_KEY = 'notificationsSeenAt'

const t = (key: string, values?: Record<string, unknown>): string =>
    values ? i18n.global.t(key, values) : i18n.global.t(key)

/** What one bill change comes to, said the way the card says it. */
const billChangeDetail = (event: IFolioEvent): string =>
    `${event.charge_description} · −${formatAmount(event.amount)} (${formatTaxRate(event.effective_basis_points)})`

/** One request's range, as a person says it: one day, or a span. Both ends inclusive. */
const absenceRange = (request: IStaffAbsenceRequest): string => {
    const from = dayjs(request.from).format('D. M. YYYY')
    const to   = dayjs(request.to).format('D. M. YYYY')

    return from === to ? from : `${from} – ${to}`
}

export const useNotifications = () => {
    const appStore   = useAppStore()
    const fetchStore = useFetchStore()

    const items = ref<INotification[]>([])

    let reading  = false
    let lastRead = 0

    /**
     * Whether this account has any source at all.
     *
     * The discount kinds hang off `folio:discount` — the right to ask for one and the
     * right to be asked are the same right seen from two ends. The rota kinds hang off
     * the staff module, and one of them hangs off nothing else at all: a change to
     * SOMEBODY'S OWN week is theirs by being in the record, not by a permission
     * (docs/OSEBJE.md §13), so a housekeeper with an empty role has a bell that fills.
     *
     * `hasModule` answers TRUE while the licence is unknown, which is right for a menu
     * (nothing is hidden by a slow read) and wrong here: it would send a request to a
     * route this hotel does not have on every reload. So it is combined with
     * `modulesLoaded`, which is the difference between "not bought" and "not asked
     * yet".
     */
    const rotaAvailable = computed((): boolean =>
        appStore.modulesLoaded && appStore.hasModule('staff'))

    const available = computed((): boolean => appStore.can('folio:discount') || rotaAvailable.value)

    const seenAt = (): string => localStorage.getItem(SEEN_KEY) ?? ''

    /** Everything currently listed has now been looked at. */
    const markSeen = (): void => localStorage.setItem(SEEN_KEY, new Date().toISOString())

    /** The requests this account may decide. Work — it does not go away by being read. */
    const loadPending = async (): Promise<INotification[]> => {
        // Guarded here and not only by `available`, which now answers true for a
        // housekeeper whose only source is her own rota. A source that asks for a route
        // its reader may not open is a 403 every minute in somebody's console.
        if (!appStore.can('folio:discount')) return []

        const { statusCode, data } = await fetchStore
            .fetch(`/properties/${appStore.propertyId}/bill-changes/pending`)
            .get()
            .json<IFolioEvent[]>()

        if (statusCode.value !== 200 || !data.value) return []

        return data.value.map((event): INotification => ({
            id:     `${NotificationKind.BillChangePending}:${event.id}`,
            kind:   NotificationKind.BillChangePending,
            icon:   'i-lucide-percent',
            color:  'warning',
            title:  t('notification.bill_change_pending'),
            detail: billChangeDetail(event),
            meta:   t('bill_change.asked_by', {
                name: event.requested_by_name || t('bill_change.person_unknown'),
                when: formatDateTime(event.occurred_at),
            }),
            at: event.occurred_at,
            to: { name: 'folio', params: { id: event.folio_id } },
        }))
    }

    /**
     * The answers to what this account asked for, since it last looked.
     *
     * The service bounds the read to a fortnight; the watermark is what makes it news.
     * A browser that has never had the bell open has no watermark and is told
     * everything in that fortnight, which is the honest answer for a first opening —
     * the alternative, starting the clock at "now", would swallow the very decision
     * somebody signed in to find.
     */
    const loadDecided = async (): Promise<INotification[]> => {
        if (!appStore.can('folio:discount')) return []

        const { statusCode, data } = await fetchStore
            .fetch(`/properties/${appStore.propertyId}/bill-changes/mine`)
            .get()
            .json<IFolioEvent[]>()

        if (statusCode.value !== 200 || !data.value) return []

        // Parsed rather than compared as text: the service writes an instant with
        // whatever sub-second precision it has and a browser writes three decimals, so
        // two strings that mean the same moment do not sort against each other.
        const since = Date.parse(seenAt())

        return data.value
            .filter(event => Number.isNaN(since) || Date.parse(event.decided_at) > since)
            .map((event): INotification => {
                const granted = event.status === FolioEventStatus.Applied

                return {
                    id:     `${NotificationKind.BillChangeDecided}:${event.id}`,
                    kind:   NotificationKind.BillChangeDecided,
                    icon:   granted ? 'i-lucide-check' : 'i-lucide-x',
                    color:  granted ? 'success' : 'neutral',
                    title:  granted
                        ? t('notification.bill_change_approved')
                        : t('notification.bill_change_rejected'),
                    detail: billChangeDetail(event),
                    meta:   t('notification.decided_by', {
                        name: event.decided_by_name || t('bill_change.person_unknown'),
                        when: formatDateTime(event.decided_at),
                    }) + (event.decision_note ? ` · ${event.decision_note}` : ''),
                    at: event.decided_at,
                    to: { name: 'folio', params: { id: event.folio_id } },
                }
            })
    }

    /**
     * Changes to MY OWN published week that I have not yet acknowledged.
     *
     * Work and not news: it stays until the person presses "Potrjujem" on their own
     * rota, which is the only event in this product that may say somebody was told
     * (docs/OSEBJE.md §7). The service answers the changes with no date range at all —
     * the week somebody is not looking at is precisely the one that catches them out —
     * so the bell shows what the screen would show if they opened it.
     */
    const loadMyRotaChanges = async (): Promise<INotification[]> => {
        if (!rotaAvailable.value) return []

        // A week is asked for because the route wants a range; the CHANGES it answers
        // are deliberately not limited to it (Logic.MyStaffRota).
        const from = dayjs().format(DATE_FORMAT)
        const to   = dayjs().add(7, 'day').format(DATE_FORMAT)

        const { statusCode, data } = await fetchStore
            .fetch(`/staff/me/shifts?from=${from}&to=${to}`)
            .get()
            .json<IStaffMyRota>()

        if (statusCode.value !== 200 || !data.value) return []

        return (data.value.changes ?? []).map((change): INotification => ({
            id:     `${NotificationKind.RotaChangeMine}:${change.id}`,
            kind:   NotificationKind.RotaChangeMine,
            icon:   shiftChangeWithdrawn(change) ? 'i-lucide-calendar-x' : 'i-lucide-calendar-clock',
            color:  shiftChangeWithdrawn(change) ? 'error' : 'warning',
            title:  t('notification.rota_change_mine'),
            detail: shiftChangeLine(change),
            meta:   change.reason
                ? `${t('staff.change_reason')}: ${change.reason}`
                : formatDateTime(change.changed_at),
            at: change.changed_at,
            to: { name: 'staff-me', query: { tab: 'rota' } },
        }))
    }

    /**
     * Changes on the rota this reader SCHEDULES that the person has not acknowledged.
     *
     * The supervisor's half of the same fact. An uncovered Saturday nobody knows about
     * is her problem long before it is the housekeeper's, and until now the only place
     * this was drawn was a strip on a screen somebody had to open.
     *
     * `staff:schedule` or `staff:oversee`, matching what the rota read itself asks for:
     * an entry that leads to a 403 teaches the desk the system is unreliable.
     *
     * # The name, and the button
     *
     * Two things were missing from this row until 2026-09-06, and both were found by
     * somebody reading a real one and asking what it meant.
     *
     * It named nobody. „Spremembe turnusa še ni potrdil — Turnus je odpovedan (bil je
     * 31. 8. 07:00–15:00) — Obvestilo ni uspelo“ tells a supervisor that a shift is
     * unconfirmed and not whose door to knock on. The name now comes off the change
     * itself (`user_name`, resolved by the service): this bell holds no personnel
     * register, and the case that needs the name most is a CANCELLED shift, which the
     * grid does not draw at all — so there would be nothing on screen to read it off
     * however clever the client were.
     *
     * And it could not be ended. This row is cleared by the PERSON pressing on their own
     * rota, and the whole reason it is in front of a supervisor is that the person could
     * not be reached — the notice failed, or they carry no channel (docs/OBVESCANJE.md
     * §5). So she tells them at the handover, and „Sporočeno osebno“ is where that gets
     * written down: it records who said it and how, stops the retries, and leaves the
     * delivery state exactly as the transport left it.
     */
    const loadUndeliveredRotaChanges = async (): Promise<INotification[]> => {
        if (!rotaAvailable.value) return []
        if (!appStore.canAny('staff:schedule', 'staff:oversee')) return []
        if (!appStore.propertyId) return []

        // The current week, as the rota screen reckons it: Monday to Monday, exclusive
        // at the far end. One helper (`weekStart`) so the bell and the screen behind it
        // cannot disagree about which week "this week" is.
        const from = weekStart(dayjs().format(DATE_FORMAT))
        const to   = dayjs(from).add(7, 'day').format(DATE_FORMAT)

        const { statusCode, data } = await fetchStore
            .fetch(`/properties/${appStore.propertyId}/staff/shifts?from=${from}&to=${to}&include_cancelled=true`)
            .get()
            .json<IStaffRota>()

        if (statusCode.value !== 200 || !data.value) return []

        const propertyId = appStore.propertyId

        return (data.value.undelivered_changes ?? []).map((change): INotification => {
            // A decline is a different row about the same record, and it is drawn as
            // one. „Spremembe turnusa še ni potrdil“ over somebody who has answered
            // „Ne morem“ is true about the transport and wrong about the morning: it
            // says nobody knows, when one person knows and cannot come — and it is the
            // shift nobody has covered that a supervisor most needs off this list.
            const declined = shiftChangeDeclined(change)

            return {
                id:     `${NotificationKind.RotaChangeUndelivered}:${change.id}`,
                kind:   NotificationKind.RotaChangeUndelivered,
                icon:   declined ? 'i-lucide-user-x' : 'i-lucide-bell-off',
                color:  declined ? 'error' : 'warning',
                title:  declined
                    ? t('notification.rota_change_declined')
                    : t('notification.rota_change_undelivered'),

                // The person first and the change after it, because the first thing a
                // supervisor needs from this row is a name. An account whose name the
                // service could not resolve falls back to the bare line rather than to a
                // colon with nothing before it.
                //
                // A decline says what the person said instead, with the change itself
                // moved down to the second line: „Ivana Sobarica je zavrnila spremembo
                // turnusa: ne morem, bolna“ is the whole news, and the interval is the
                // detail she reads next.
                detail: declined ? shiftChangeDeclineLine(change) : shiftChangeForPerson(change),
                meta:   declined
                    ? `${shiftChangeTag(change).label} · ${shiftChangeLine(change)}`
                    : shiftChangeTag(change).label,
                at: change.changed_at,
                to: { name: 'staff-rota' },

                // The button stays on a declined row, and that is deliberate: what a
                // supervisor DOES about „Ne morem“ is find cover and tell the person, and
                // „Sporočeno osebno“ is where that gets written down. Without it a
                // declined change would sit on this list for ever, which is the permanent
                // row the whole in-person path was built to remove.
                action: {
                    label: t('staff.told_in_person'),
                    icon:  'i-lucide-message-square-text',
                    run:   () => acknowledgeShiftChangeInPerson(propertyId, change.id),
                },
            }
        })
    }

    /**
     * Requests for leave waiting for a decision — the manager's half of §10.
     *
     * Work, not news: it stays until somebody answers it. `staff:approve`, matching what
     * the queue itself asks for — an entry that leads to a 403 teaches the desk that the
     * system is unreliable.
     *
     * A request the reader made THEMSELVES is left out. Nobody decides their own (§9's
     * rule, §10's decisions), so it would be a row with nothing to press and a sentence
     * explaining why not, on a bell whose whole value is that everything in it is
     * actionable.
     */
    const loadPendingAbsenceRequests = async (): Promise<INotification[]> => {
        if (!rotaAvailable.value) return []
        if (!appStore.can('staff:approve')) return []
        if (!appStore.propertyId) return []

        const requests = await loadAbsenceRequests(appStore.propertyId, StaffAbsenceState.Requested)
        const me       = appStore.userData?.id ?? ''

        return requests
            .filter(request => request.requested_by !== me)
            .map((request): INotification => ({
                id:     `${NotificationKind.AbsenceRequestPending}:${request.id}`,
                kind:   NotificationKind.AbsenceRequestPending,
                icon:   absenceKindIcon(request.kind),
                color:  'warning',
                title:  t('notification.absence_request_pending'),
                detail: `${request.user_name} · ${absenceKindLabel(request.kind)} · ${absenceRange(request)}`,
                meta:   request.note || formatDateTime(request.requested_at),
                at:     request.requested_at,
                to:     { name: 'staff-absences' },
            }))
    }

    /**
     * The answer to leave THIS account asked for.
     *
     * News rather than work: counted until the bell is opened and gone afterwards, like
     * the discount decisions above. A refusal is in it as loudly as a grant — §10 keeps a
     * refused request visible for the reason §9 does, and somebody who is not told is
     * somebody who has booked a flight.
     *
     * It needs no permission at all: asking for leave is the other side of being in the
     * record (§13), so a housekeeper with an empty role has a bell that fills.
     */
    const loadDecidedAbsenceRequests = async (): Promise<INotification[]> => {
        if (!rotaAvailable.value) return []

        const requests = await loadMyAbsenceRequests()
        const since    = Date.parse(seenAt())

        return requests
            .filter(request => request.state === StaffAbsenceState.Approved
                || request.state === StaffAbsenceState.Rejected)
            .filter(request => Number.isNaN(since) || Date.parse(request.decided_at) > since)
            .map((request): INotification => {
                const granted = request.state === StaffAbsenceState.Approved

                return {
                    id:     `${NotificationKind.AbsenceRequestDecided}:${request.id}`,
                    kind:   NotificationKind.AbsenceRequestDecided,
                    icon:   granted ? 'i-lucide-check' : 'i-lucide-x',
                    color:  granted ? 'success' : 'error',
                    title:  granted
                        ? t('notification.absence_approved')
                        : t('notification.absence_rejected'),
                    detail: `${absenceKindLabel(request.kind)} · ${absenceRange(request)}`,
                    meta:   request.decision_note || formatDateTime(request.decided_at),
                    at:     request.decided_at,
                    to:     { name: 'staff-me', query: { tab: 'vacation' } },
                }
            })
    }

    /**
     * The yearly leave decision issued to THIS account and not yet acknowledged
     * (docs/OSEBJE-UI.md §5).
     *
     * Work and not news, exactly like an unacknowledged shift change: it stays until the
     * person presses „Potrjujem“ on their own page, and that press is the only event in
     * this product that may say somebody was told (§7's rule, one domain over). A house
     * that issues forty decisions in January and hears nothing back has forty people who
     * have not read them, and the bell is what makes that a fact rather than an
     * assumption.
     *
     * No permission at all: a decision about one's own leave is one's own by being in the
     * record (§13), so a housekeeper with an empty role has a bell that fills.
     */
    const loadMyVacationDecisions = async (): Promise<INotification[]> => {
        if (!rotaAvailable.value) return []

        const decisions = await loadMyUnacknowledgedDecisions()

        return decisions
            .filter(decision => !isAcknowledged(decision))
            .map((decision): INotification => ({
                id:     `${NotificationKind.VacationDecisionIssued}:${decision.id}`,
                kind:   NotificationKind.VacationDecisionIssued,
                icon:   'i-lucide-palmtree',
                color:  'warning',
                title:  t('notification.vacation_decision_issued'),
                detail: t('staff_vacation.decision_line', {
                    year: decision.year,
                    days: formatDays(decision.total_days),
                }),
                meta:   t('staff_vacation.issued_at', {
                    who: decision.issued_by_name || '—',
                    at:  formatDateTime(decision.issued_at),
                }),
                at: decision.issued_at,
                to: { name: 'staff-me', query: { tab: 'vacation' } },
            }))
    }

    /** Every source of a row, in one place. A new kind is a line here. */
    const sources = [
        loadPending,
        loadDecided,
        loadMyRotaChanges,
        loadUndeliveredRotaChanges,
        loadPendingAbsenceRequests,
        loadDecidedAbsenceRequests,
        loadMyVacationDecisions,
    ]

    /**
     * Deliberately quiet. No loader and no toast: a failed background read is the list
     * we already had, and telling a receptionist about our plumbing is not news either.
     * A 401 is the one exception, and the fetch store handles that for every call.
     */
    const reload = async (force = false): Promise<void> => {
        if (reading) return

        if (!appStore.isLoggedIn() || !appStore.propertyId || !available.value) {
            items.value = []

            return
        }

        const now = Date.now()

        if (!force && now - lastRead < MIN_GAP_MS) return

        reading  = true
        lastRead = now

        try {
            const answered = await Promise.all(sources.map(source => source()))

            items.value = answered
                .flat()
                .sort((first, second) => Date.parse(second.at) - Date.parse(first.at))
        } finally {
            reading = false
        }
    }

    const timer = window.setInterval(() => { void reload() }, EVERY_MS)

    onUnmounted(() => window.clearInterval(timer))

    return { items, available, reload, markSeen }
}
